import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { http, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Sparkles, Shield, Code2, Eye } from "lucide-react";
import ModelSelector from "@/components/ModelSelector";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Builder() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const initialPrompt = searchParams.get("prompt") || "";
  const { user, refreshUser } = useAuth();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [modelId, setModelId] = useState("openai:gpt-5.6-terra");
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  const scrollRef = useRef(null);
  const autoSentRef = useRef(false);

  useEffect(() => {
    if (!projectId) return;
    http.get(`/projects/${projectId}`).then((r) => {
      setProject(r.data);
      setModelId(r.data.model_id || "openai:gpt-5.6-terra");
    });
    http.get(`/projects/${projectId}/messages`).then((r) => setMessages(r.data.messages || []));
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamBuffer]);

  const send = async (textOverride) => {
    const text = (textOverride ?? prompt).trim();
    if (!text || streaming) return;
    setStreaming(true);
    setStreamBuffer("");
    setMessages((m) => [...m, { role: "user", content: text, id: `tmp-${Date.now()}` }]);
    setPrompt("");

    try {
      const token = localStorage.getItem("mf_token");
      const resp = await fetch(`${API}/builder/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: text, model_id: modelId, project_id: projectId }),
      });
      if (!resp.ok || !resp.body) {
        if (resp.status === 402) {
          const err = await resp.json().catch(() => ({}));
          toast.error(err.detail || "Not enough credits. Top up to continue.", {
            action: { label: "Top up", onClick: () => (window.location.href = "/pricing") },
          });
        } else {
          toast.error("Stream failed to start");
        }
        // remove the temp user message we optimistically appended
        setMessages((m) => m.filter((x) => x.id.startsWith("tmp-") === false || x.role !== "user" || x.content !== text));
        setStreaming(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const chunk of parts) {
          const lines = chunk.split("\n");
          let ev = "message";
          const data = [];
          for (const l of lines) {
            if (l.startsWith("event:")) ev = l.slice(6).trim();
            else if (l.startsWith("data:")) {
              // SSE spec: strip only the single leading space after "data:"
              let d = l.slice(5);
              if (d.startsWith(" ")) d = d.slice(1);
              data.push(d);
            }
          }
          const payload = data.join("\n");
          if (ev === "done") continue;
          if (ev === "error") { toast.error(payload); continue; }
          // restore newlines that were escaped server-side
          const restored = payload.replace(/\\n/g, "\n");
          acc += restored;
          setStreamBuffer(acc);
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: acc, id: `tmp-a-${Date.now()}` }]);
      setStreamBuffer("");
      refreshUser();
    } catch (err) {
      toast.error("Streaming error");
    } finally {
      setStreaming(false);
    }
  };

  // Auto-send the initial prompt if present (from template gallery)
  useEffect(() => {
    if (initialPrompt && !autoSentRef.current && project && messages.length === 0) {
      autoSentRef.current = true;
      send(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, project, messages.length]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center">
        <h2 className="font-display text-2xl font-bold">Sign in required</h2>
        <p className="text-slate-600 mt-2">Please log in to use the builder.</p>
      </div>
    );
  }

  const scaffoldText = messages.filter((m) => m.role === "assistant").map((m) => m.content).join("\n\n---\n\n") + (streamBuffer ? "\n\n" + streamBuffer : "");

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6" data-testid="ai-chat-builder">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-mono uppercase text-emerald-800">Builder</p>
          <h1 className="font-display text-xl lg:text-2xl font-bold text-slate-900" data-testid="builder-project-title">
            {project?.title || "Loading..."}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-1 text-xs font-mono text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1">
            <Shield className="h-3 w-3" /> PHI hints ON
          </div>
          <ModelSelector value={modelId} onChange={setModelId} />
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4 h-[calc(100vh-190px)] min-h-[560px]">
        {/* Chat pane */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-700" />
            <span className="font-display font-semibold text-slate-900">Design chat</span>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && !streamBuffer && (
              <div className="text-sm text-slate-500 space-y-3">
                <p>Describe the healthcare app you want to build. ViewBox AI will scaffold roles, screens, data model, and API endpoints.</p>
                <div className="space-y-1">
                  {[
                    "Build a patient portal for a pediatric clinic with vaccine reminders",
                    "Create a mental health check-in app with PHQ-9 scoring",
                    "Scaffold a telemedicine consult app with encrypted video",
                  ].map((s) => (
                    <button key={s} onClick={() => setPrompt(s)}
                      data-testid={`suggestion-${s.slice(0,20).replace(/\s+/g,'-')}`}
                      className="w-full text-left text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-md px-3 py-2 transition-colors">
                      → {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-emerald-900 text-white ml-8" : "bg-slate-50 text-slate-800 mr-8"}`} data-testid={`msg-${m.role}`}>
                <div className={`text-[10px] font-mono uppercase mb-1 ${m.role === "user" ? "text-emerald-200" : "text-slate-500"}`}>{m.role}</div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              </div>
            ))}
            {streaming && streamBuffer && (
              <div className="rounded-xl px-3 py-2 text-sm bg-slate-50 text-slate-800 mr-8" data-testid="streaming-message">
                <div className="text-[10px] font-mono uppercase mb-1 text-slate-500">assistant</div>
                <div className="whitespace-pre-wrap leading-relaxed mf-typing">{streamBuffer}</div>
              </div>
            )}
          </div>
          <div className="p-3 border-t border-slate-200">
            <div className="flex items-end gap-2">
              <Textarea rows={2} placeholder="Describe your idea, or refine the scaffold..."
                data-testid="input-chat-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                className="resize-none" />
              <Button onClick={() => send()} disabled={streaming || !prompt.trim()}
                className="bg-emerald-900 hover:bg-emerald-800 text-white h-10"
                data-testid="btn-send-prompt">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Preview pane */}
        <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
          <Tabs defaultValue="scaffold" className="flex-1 flex flex-col">
            <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="scaffold" data-testid="tab-scaffold"><Code2 className="h-3.5 w-3.5 mr-1"/>Scaffold</TabsTrigger>
                <TabsTrigger value="preview" data-testid="tab-preview"><Eye className="h-3.5 w-3.5 mr-1"/>Preview</TabsTrigger>
              </TabsList>
              <span className="text-xs font-mono text-slate-500">{streaming ? "generating..." : "ready"}</span>
            </div>
            <TabsContent value="scaffold" className="flex-1 overflow-y-auto m-0">
              <pre className="mf-code-pane min-h-full p-5 whitespace-pre-wrap" data-testid="scaffold-output">
{scaffoldText || "// Your generated scaffold will appear here.\n// Start by describing your healthcare app in the chat."}
              </pre>
            </TabsContent>
            <TabsContent value="preview" className="flex-1 overflow-y-auto m-0 p-6 bg-slate-50">
              <div className="mx-auto max-w-xl bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="ui-preview">
                <div className="flex items-center gap-2 pb-4 border-b border-slate-100">
                  <div className="h-8 w-8 rounded-lg bg-emerald-900 text-white flex items-center justify-center text-xs font-bold">M</div>
                  <div>
                    <p className="font-display font-semibold text-slate-900 text-sm">{project?.title || "Your Healthcare App"}</p>
                    <p className="text-xs text-slate-500">Preview · {modelId.split(":")[0]}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                    <p className="text-xs font-mono uppercase text-emerald-800">Patient Snapshot</p>
                    <p className="mt-1 text-sm text-slate-800">Jamie Rivera · 34 · Primary Care</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-[10px] uppercase text-slate-500">BP</p>
                      <p className="font-display font-semibold text-slate-900">118 / 76</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-[10px] uppercase text-slate-500">Next visit</p>
                      <p className="font-display font-semibold text-slate-900">Tue · 10:30</p>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-[10px] uppercase text-slate-500 mb-1">Compliance</p>
                    <div className="flex items-center gap-1 text-xs text-sky-700"><Shield className="h-3 w-3"/>HIPAA-aware · PHI encrypted</div>
                  </div>
                </div>
                <p className="mt-6 text-[11px] text-slate-400 text-center">Live UI preview is a static illustration in demo mode.</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
